/**
 * Inbound List Component
 *
 * Displays list of inbound warehouse movements (from Purchase Orders)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import {
  ChevronDown,
  ChevronUp,
  Package,
  ExternalLink,
  Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import warehouseService from '../../services/warehouseService';
import TemplateSelector from '../TemplateSelector';
import excelExportService from '../../services/excelExportService';
import { formatBeijingDate, formatBeijingDateTime } from '../../utils/dateUtils';
import FilterBar from './FilterBar';
import useDebounce from '../../hooks/useDebounce';
import useFilterPersistence from '../../hooks/useFilterPersistence';

// Calculate default date range (last 30 days)
const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0]
  };
};

function InboundList({ refreshTrigger, onRefresh, productTypeFilter = 'all' }) {
  const navigate = useNavigate();
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  // Use filter persistence hook
  const [persistedState, setPersistedState] = useFilterPersistence('warehouse-inbound', {
    page: 0,
    rowsPerPage: 10,
    dateRange: getDefaultDateRange(),
    filters: {
      poName: '',
      clientName: '',
      sourceType: 'all'
    }
  });

  // Extract state from persisted state
  const page = persistedState.page;
  const rowsPerPage = persistedState.rowsPerPage;
  const dateRange = persistedState.dateRange;
  const filters = persistedState.filters;

  // Update functions
  const setPage = (newPage) => {
    setPersistedState(prev => ({
      ...prev,
      page: typeof newPage === 'function' ? newPage(prev.page) : newPage
    }));
  };

  const setRowsPerPage = (newRowsPerPage) => {
    setPersistedState(prev => ({
      ...prev,
      rowsPerPage: typeof newRowsPerPage === 'function' ? newRowsPerPage(prev.rowsPerPage) : newRowsPerPage
    }));
  };

  const setDateRange = (newDateRange) => {
    setPersistedState(prev => ({
      ...prev,
      dateRange: typeof newDateRange === 'function' ? newDateRange(prev.dateRange) : newDateRange
    }));
  };

  const setFilters = (newFilters) => {
    setPersistedState(prev => ({
      ...prev,
      filters: typeof newFilters === 'function' ? newFilters(prev.filters) : newFilters
    }));
  };

  // Debounce filter values for performance (500ms delay)
  const debouncedPOName = useDebounce(filters.poName, 500);
  const debouncedClientName = useDebounce(filters.clientName, 500);

  const [expandedRows, setExpandedRows] = useState({});
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);

  // Load inbound movements
  const loadMovements = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        movement_type: 'inbound',
        page: page + 1,
        page_size: rowsPerPage
      };

      // Add date range filters - convert to UTC
      if (dateRange.startDate) {
        // Convert YYYY-MM-DD to UTC start of day (00:00:00 UTC)
        const startDate = new Date(dateRange.startDate + 'T00:00:00');
        params.date_from = startDate.toISOString();
      }
      if (dateRange.endDate) {
        // Convert YYYY-MM-DD to UTC end of day (23:59:59 UTC)
        const endDate = new Date(dateRange.endDate + 'T23:59:59');
        params.date_to = endDate.toISOString();
      }

      // Add PO filter (using debounced value)
      if (debouncedPOName) {
        params.po_name = debouncedPOName;
      }

      // Add client name filter (using debounced value)
      if (debouncedClientName) {
        params.client_name = debouncedClientName;
      }

      // Add product type filter
      if (productTypeFilter && productTypeFilter !== 'all') {
        params.product_type = productTypeFilter;
      }

      // Add source type filter
      if (filters.sourceType && filters.sourceType !== 'all') {
        params.source_type = filters.sourceType;
      }

      const response = await warehouseService.getMovements(params);
      setMovements(response.movements || []);
      setTotal(response.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to load inbound movements:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, dateRange, debouncedPOName, debouncedClientName, productTypeFilter, filters.sourceType]);

  useEffect(() => {
    loadMovements();
  }, [loadMovements, refreshTrigger]);

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0); // Reset to first page when date range changes
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      poName: '',
      clientName: '',
      sourceType: 'all'
    });
    setDateRange(getDefaultDateRange());
    setPage(0);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleExpand = (movementId) => {
    setExpandedRows(prev => ({
      ...prev,
      [movementId]: !prev[movementId]
    }));
  };

  const handleViewPurchaseOrder = (poId) => {
    navigate(`/purchases/${poId}/view`);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: '待确认', variant: 'outline' },
      confirmed: { label: '已确认', variant: 'default' },
      completed: { label: '已完成', variant: 'default' },
      cancelled: { label: '已取消', variant: 'destructive' }
    };

    const config = statusConfig[status] || { label: status, variant: 'outline' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Get source type badge
  const getSourceTypeBadge = (sourceType) => {
    const sourceTypeConfig = {
      purchase_order: { label: '采购入库', variant: 'default', color: 'bg-blue-100 text-blue-800' },
      production_order: { label: '成品入库', variant: 'default', color: 'bg-green-100 text-green-800' },
      remaining_bar: { label: '余料入库', variant: 'default', color: 'bg-amber-100 text-amber-800' },
      sales_order: { label: '销售退货', variant: 'default', color: 'bg-purple-100 text-purple-800' },
      manual: { label: '手动入库', variant: 'outline', color: 'bg-gray-100 text-gray-800' },
      cross_client_transfer: { label: '调拨入库', variant: 'default', color: 'bg-indigo-100 text-indigo-800' }
    };

    const config = sourceTypeConfig[sourceType] || { label: sourceType || '其他', variant: 'outline', color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Get source reference display
  const getSourceReference = (movement) => {
    const { source_type, source_reference, source_id, products } = movement;
    
    if (source_type === 'purchase_order' && source_id) {
      return (
        <button
          onClick={() => handleViewPurchaseOrder(source_id)}
          className="flex items-center gap-1 text-primary hover:underline"
        >
          {source_reference || 'PO'}
          <ExternalLink className="h-3 w-3" />
        </button>
      );
    }
    
    if (source_type === 'production_order' && source_reference) {
      return (
        <span className="text-green-600 font-medium">
          {source_reference}
        </span>
      );
    }
    
    if (source_type === 'remaining_bar' && products && products.length > 0) {
      // Show the remaining product info
      const remProduct = products[0];
      return (
        <span className="text-amber-600">
          {remProduct.product_id} ({remProduct.quantity}根)
        </span>
      );
    }
    
    if (source_type === 'manual') {
      return <span className="text-gray-500">手动创建</span>;
    }
    
    return '-';
  };

  const calculateTotalQuantity = (products) => {
    if (!products || products.length === 0) return 0;
    return products.reduce((sum, product) => sum + (product.quantity_received || product.quantity || 0), 0);
  };

  const calculateTotalValue = (products) => {
    if (!products || products.length === 0) return 0;
    return products.reduce((sum, product) => {
      const qty = product.quantity_received || product.quantity || 0;
      const price = product.unit_price || 0;
      return sum + (qty * price);
    }, 0);
  };

  const [selectedMovement, setSelectedMovement] = useState(null);

  const handleExportClick = (movement) => {
    setSelectedMovement(movement);
    setTemplateSelectorOpen(true);
  };

  const handleTemplateSelectorClose = () => {
    setTemplateSelectorOpen(false);
    setSelectedMovement(null);
  };

  const handleTemplateSelected = async (template) => {
    const movementToExport = selectedMovement;
    setTemplateSelectorOpen(false);
    setSelectedMovement(null);

    try {
      const templateId = template?.template_id;
      if (!templateId) {
        alert('未选择模板');
        return;
      }

      if (!movementToExport) {
        alert('未选择入库单');
        return;
      }

      const { blob, filename } = await excelExportService.exportUsingTemplate('warehouse_inbound', {
        template_id: templateId,
        movement_ids: [movementToExport._id],
      });

      // Download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert(`导出失败: ${err.message}`);
    }
  };

  const totalPages = Math.ceil(total / rowsPerPage);

  return (
    <div>
      {/* Filter Bar */}
      <FilterBar
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        showPOFilter={true}
        showSourceTypeFilter={true}
      />

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted border-b">
              <tr>
                <th className="w-12 p-3" />
                <th className="text-left p-3 font-medium">单号</th>
                <th className="text-left p-3 font-medium">状态</th>
                <th className="text-left p-3 font-medium">来源类型</th>
                <th className="text-left p-3 font-medium">来源单号</th>
                <th className="text-left p-3 font-medium">供应商</th>
                <th className="text-left p-3 font-medium">客户</th>
                <th className="text-left p-3 font-medium">仓库</th>
                <th className="text-left p-3 font-medium">总数量</th>
                <th className="text-left p-3 font-medium">日期</th>
                <th className="text-right p-3 font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="text-center p-8">
                    <span className="text-muted-foreground">加载中...</span>
                  </td>
                </tr>
              ) : movements.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center p-8">
                    <div className="flex flex-col items-center py-4">
                      <Package className="h-12 w-12 text-muted-foreground mb-2" />
                      <span className="text-muted-foreground">暂无入库记录</span>
                    </div>
                  </td>
                </tr>
              ) : (
                movements.map((movement) => (
                  <Collapsible
                    key={movement._id}
                    open={expandedRows[movement._id]}
                    onOpenChange={() => handleToggleExpand(movement._id)}
                    asChild
                  >
                    <React.Fragment>
                      <tr className="border-b hover:bg-muted">
                        <td className="p-3">
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              {expandedRows[movement._id] ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </td>
                        <td className="p-3">
                          <span className="font-medium">
                            {movement._id.slice(-6).toUpperCase()}
                          </span>
                        </td>
                        <td className="p-3">{getStatusBadge(movement.status)}</td>
                        <td className="p-3">
                          {getSourceTypeBadge(movement.source_type)}
                        </td>
                        <td className="p-3">
                          {getSourceReference(movement)}
                        </td>
                        <td className="p-3">{movement.supplier_name || '-'}</td>
                        <td className="p-3">{movement.client_name || '-'}</td>
                        <td className="p-3">{movement.warehouse_location || 'main'}</td>
                        <td className="p-3">{calculateTotalQuantity(movement.products)}</td>
                        <td className="p-3">
                          {formatBeijingDate(movement.movement_date)}
                        </td>
                        <td className="p-3 text-right">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleExportClick(movement)}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>导出</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                      </tr>

                      {/* Expanded Row - Product Details */}
                      <tr>
                        <td colSpan={10} className="p-0">
                          <CollapsibleContent>
                            <div className="p-4 bg-muted border-b">
                              <h4 className="text-sm font-semibold mb-3">入库产品明细</h4>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b">
                                    <th className="text-left p-2 font-medium">产品编码</th>
                                    <th className="text-left p-2 font-medium">产品名称</th>
                                    <th className="text-left p-2 font-medium">订购数量</th>
                                    <th className="text-left p-2 font-medium">收货数量</th>
                                    <th className="text-left p-2 font-medium">单位</th>
                                    <th className="text-left p-2 font-medium">单价</th>
                                    <th className="text-left p-2 font-medium">小计</th>
                                    <th className="text-left p-2 font-medium">库位</th>
                                    <th className="text-left p-2 font-medium">批次号</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {movement.products?.map((product, index) => (
                                    <tr key={index} className="border-b">
                                      <td className="p-2">{product.product_id}</td>
                                      <td className="p-2">{product.name}</td>
                                      <td className="p-2">{product.quantity || 0}</td>
                                      <td className="p-2">
                                        <span
                                          className={
                                            product.quantity_received < product.quantity
                                              ? 'text-warning'
                                              : product.quantity_received > product.quantity
                                              ? 'text-destructive'
                                              : ''
                                          }
                                        >
                                          {product.quantity_received}
                                        </span>
                                      </td>
                                      <td className="p-2">{product.unit}</td>
                                      <td className="p-2">¥{product.unit_price?.toFixed(2) || '0.00'}</td>
                                      <td className="p-2">
                                        ¥{((product.quantity_received || 0) * (product.unit_price || 0)).toFixed(2)}
                                      </td>
                                      <td className="p-2">{product.location || movement.warehouse_location}</td>
                                      <td className="p-2">{product.batch_number || '-'}</td>
                                    </tr>
                                  ))}
                                  <tr>
                                    <td colSpan={6} className="p-2 text-right font-medium">
                                      合计:
                                    </td>
                                    <td className="p-2 font-medium">
                                      ¥{calculateTotalValue(movement.products).toFixed(2)}
                                    </td>
                                    <td colSpan={2} />
                                  </tr>
                                </tbody>
                              </table>
                              {movement.notes && (
                                <p className="mt-3 text-sm text-muted-foreground">
                                  备注: {movement.notes}
                                </p>
                              )}
                              {movement.created_by && (
                                <p className="mt-2 text-xs text-muted-foreground">
                                  创建时间: {formatBeijingDateTime(movement.created_at)}
                                </p>
                              )}
                            </div>
                          </CollapsibleContent>
                        </td>
                      </tr>
                    </React.Fragment>
                  </Collapsible>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t bg-card">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">每页显示</span>
          <Select value={String(rowsPerPage)} onValueChange={(val) => handleChangeRowsPerPage({ target: { value: val } })}>
            <SelectTrigger className="w-20 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, total)} / {total}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleChangePage(page - 1)}
            disabled={page === 0}
          >
            上一页
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleChangePage(page + 1)}
            disabled={page >= totalPages - 1}
          >
            下一页
          </Button>
        </div>
      </div>

      {/* Template Selector */}
      <TemplateSelector
        open={templateSelectorOpen}
        onClose={handleTemplateSelectorClose}
        onSelect={handleTemplateSelected}
        entityType="warehouse_inbound"
        title="选择入库单导出模板"
        allowManagement={true}
      />
    </div>
  );
}

export default InboundList;
